import type { ReactNode } from 'react'
import { Lock } from 'lucide-react'
import styles from './CoverCard.module.css'

interface CoverCardProps {
  /** Название: вторая, крупная строка. */
  title: ReactNode
  /** Надпись над названием: тип карточки, раздел, длительность. */
  caption?: string
  image?: string
  /** Тег поверх фото — например, «Без глютена». */
  tag?: string
  /** Закрытая карточка: показываем замок, как в макете. */
  isLocked?: boolean
  /** Широкий вариант, 260px. */
  isWide?: boolean
  /** Занять всю ячейку: размер задаёт контейнер, а не карточка. */
  isFluid?: boolean
  /** Карточка внутри ссылки — клик и фокус держит она, а не карточка. */
  isStatic?: boolean
  /** Поверх обложки: таймер доступности и прочее. */
  overlay?: ReactNode
  /** Правый верхний угол: избранное или другое действие. */
  action?: ReactNode
  /** Низ карточки под названием: прогресс курса и прочее. */
  footer?: ReactNode
  onClick?: () => void
}

/**
 * Карточка с обложкой во всю площадь и затемнением снизу.
 *
 * Одна на все блоки главной — рецепты, разделы, курсы, недавние: в макете они
 * отличаются только содержимым углов и подписей, а не вёрсткой.
 */
export const CoverCard = ({
  title,
  caption,
  image,
  tag,
  isLocked,
  isWide,
  isFluid,
  isStatic,
  overlay,
  action,
  footer,
  onClick,
}: CoverCardProps) => (
  // Корень не <button>: внутри карточки живёт своя кнопка избранного, а
  // вложенные кнопки невалидны и ломают клик в части браузеров.
  <div
    className={`${styles.card} ${isWide ? styles.wide : ''} ${isFluid ? styles.fluid : ''}`}
    role={isStatic ? undefined : 'button'}
    tabIndex={isStatic ? undefined : 0}
    onClick={isStatic ? undefined : onClick}
    onKeyDown={event => {
      if (isStatic) return
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      onClick?.()
    }}
  >
    {image && (
      <img className={styles.cover} src={image} alt="" loading="lazy" />
    )}
    <span className={styles.shade} />
    {overlay}

    <span className={styles.header}>
      <span className={styles.headerLeft}>
        {isLocked && (
          <span className={styles.lock}>
            <Lock size={14} aria-hidden />
          </span>
        )}
        {tag && <span className={styles.tag}>{tag}</span>}
      </span>
      {action}
    </span>

    <span className={styles.info}>
      {caption && <span className={styles.caption}>{caption}</span>}
      <span className={styles.title}>{title}</span>
      {footer}
    </span>
  </div>
)
